import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MarkdownStreamingContext } from './CodeBlock';
import { parseCopyBlock } from '../../utils/chatCopyBlocks';
import { markdownToClipboard, writeClipboard } from '../../utils/answerClipboard';
import { trackChatUxEvent } from '../../utils/eventTracker';
import './CopyBlock.css';

const isChatPage = () => window.location.pathname.startsWith('/chat');

const CopyIcon = () => (
  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

/** ```wq-copy 복사용 글 카드 — 생기부 문구·학부모 문자·가정통신문처럼 그대로 붙여 넣을 글. */
const CopyBlock = ({ code }) => {
  const streaming = useContext(MarkdownStreamingContext);
  const { title, body } = useMemo(() => parseCopyBlock(code), [code]);
  const clip = useMemo(() => markdownToClipboard(body, { hardBreaks: true }), [body]);
  const [state, setState] = useState('idle');
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!clip.plain && !streaming) return null;

  const handleCopy = async () => {
    try {
      const format = await writeClipboard(clip);
      setState('copied');
      if (isChatPage()) trackChatUxEvent('message_copy', { kind: 'block', format, responseChars: clip.plain.length });
    } catch (error) {
      console.error('복사 실패:', error);
      setState('failed');
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState('idle'), 2000);
  };

  const label = title || '복사용 글';
  const copied = state === 'copied';

  return (
    <section aria-label={label} className="wq-copy">
      <div className="wq-copy__head">
        <div className="wq-copy__meta">
          <span className="wq-copy__title">{label}</span>
          <span className="wq-copy__count">공백 포함 {Array.from(clip.plain).length.toLocaleString('ko-KR')}자</span>
        </div>
        <button
          aria-label={copied ? `${label} 복사 완료` : `${label} 복사`}
          className={`wq-copy__button ${copied ? 'is-copied' : ''}`}
          disabled={streaming}
          onClick={handleCopy}
          title={streaming ? '답변이 끝나면 복사할 수 있어요' : undefined}
          type="button"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          <span>{copied ? '복사됨' : state === 'failed' ? '복사 실패' : '복사'}</span>
        </button>
      </div>
      <div className="wq-copy__body">{clip.plain}</div>
    </section>
  );
};

export default CopyBlock;
